import * as React from "react"
import {
  useChartBase,
  injectStyle,
  resolveColor,
  CHART_TONE,
  type NumberFormatter,
} from "./chartStyles"

/*
 * MARK V — BarChart. Barras verticais em flex (escalam nítidas, sem texto SVG).
 * Cada dado pode ter `compare` (barra de referência agrupada, ex.: orçado vs
 * realizado), `tone`, `color` explícito ou `highlight`. Porte fiel de
 * mark-v-design-system/components/charts/BarChart.jsx.
 */

const CSS = `
.mv-bar{ position:relative; }
.mv-bar__plot{ position:relative; display:flex; align-items:flex-end; }
.mv-bar__grid{ position:absolute; inset:0 0 0 0; pointer-events:none; }
.mv-bar__gl{ position:absolute; left:0; right:0; border-top:1px solid var(--markv-chart-grid); }
.mv-bar__gl--axis{ border-top-color:var(--markv-chart-axis); }
.mv-bar__yaxis{ position:absolute; left:0; top:0; bottom:0; width:var(--mv-bar-gutter,0px); }
.mv-bar__yt{ position:absolute; right:8px; transform:translateY(-50%); }
.mv-bar__cols{ position:relative; display:flex; align-items:flex-end; width:100%; height:100%; z-index:1; }
.mv-bar__col{ flex:1; display:flex; flex-direction:column; align-items:center; justify-content:flex-end;
  height:100%; min-width:0; }
.mv-bar__group{ display:flex; align-items:flex-end; justify-content:center; gap:5px; width:100%; height:100%; }
.mv-bar__bar{ border-radius:4px 4px 0 0; min-height:2px; position:relative;
  transition:filter var(--markv-duration) var(--markv-ease); }
.mv-bar__bar:hover{ filter:brightness(1.06); }
.mv-bar__v{ position:absolute; left:50%; transform:translateX(-50%); bottom:calc(100% + 4px);
  white-space:nowrap; font-size:var(--markv-text-2xs); }
.mv-bar__cats{ display:flex; margin-top:8px; }
.mv-bar__cat{ flex:1; min-width:0; padding:0 2px; }
`

export type BarDatum = {
  label: string
  value: number
  compare?: number
  tone?: string
  color?: string
  highlight?: boolean
}

export type BarChartProps = {
  data: BarDatum[]
  height?: number
  max?: number
  showValues?: boolean
  format?: NumberFormatter
  series?: [string, string]
  yTicks?: number
  className?: string
}

export function BarChart({
  data,
  height = 200,
  max,
  showValues = true,
  format = (v) => v,
  series,
  yTicks = 0,
  className = "",
}: BarChartProps) {
  useChartBase()
  React.useEffect(() => injectStyle("mv-bar-styles", CSS), [])

  const vals = data.flatMap((d) => [d.value, ...(d.compare != null ? [d.compare] : [])])
  const top = max ?? Math.max(...vals, 0)
  const pct = (v: number) => (top > 0 ? Math.max(0, (v / top) * 100) : 0)
  const gutter = yTicks > 0 ? 44 : 0

  const ticks: number[] = []
  if (yTicks > 0) {
    for (let i = 0; i <= yTicks; i++) ticks.push((top / yTicks) * i)
  }

  const barColor = (d: BarDatum) =>
    d.highlight ? CHART_TONE.highlight : resolveColor(d.color ?? d.tone, CHART_TONE.primary)

  const gridLines = ticks.length ? ticks : [top, top * 0.5, 0]

  return (
    <div className={`mv-chart mv-bar ${className}`.trim()}>
      <div className="mv-bar__plot" style={{ height, paddingLeft: gutter }}>
        <div className="mv-bar__grid" style={{ left: gutter }}>
          {gridLines.map((t, i, arr) => (
            <div
              key={i}
              className={`mv-bar__gl ${i === arr.length - 1 ? "mv-bar__gl--axis" : ""}`.trim()}
              style={{ bottom: `${pct(t)}%` }}
            />
          ))}
        </div>
        {yTicks > 0 && (
          <div
            className="mv-bar__yaxis"
            style={{ "--mv-bar-gutter": `${gutter}px` } as React.CSSProperties}
          >
            {ticks.map((t, i) => (
              <span key={i} className="mv-chart__tick mv-bar__yt" style={{ bottom: `${pct(t)}%` }}>
                {format(Math.round(t))}
              </span>
            ))}
          </div>
        )}
        <div className="mv-bar__cols">
          {data.map((d, i) => (
            <div className="mv-bar__col" key={i}>
              <div className="mv-bar__group">
                {d.compare != null && (
                  <div
                    className="mv-bar__bar"
                    style={{ height: `${pct(d.compare)}%`, width: "42%", background: CHART_TONE.compare }}
                    title={`${series ? series[1] : "ref"}: ${format(d.compare)}`}
                  />
                )}
                <div
                  className="mv-bar__bar"
                  style={{
                    height: `${pct(d.value)}%`,
                    width: d.compare != null ? "42%" : "62%",
                    background: barColor(d),
                  }}
                  title={`${series ? series[0] : d.label}: ${format(d.value)}`}
                >
                  {showValues && <span className="mv-bar__v mv-chart__val">{format(d.value)}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mv-bar__cats" style={{ paddingLeft: gutter }}>
        {data.map((d, i) => (
          <div className="mv-bar__cat mv-chart__cat" key={i}>
            {d.label}
          </div>
        ))}
      </div>
      {series && (
        <div className="mv-chart__legend">
          <span className="mv-chart__lg">
            <span className="mv-chart__sw" style={{ background: CHART_TONE.primary }} />
            {series[0]}
          </span>
          <span className="mv-chart__lg">
            <span className="mv-chart__sw" style={{ background: CHART_TONE.compare }} />
            {series[1]}
          </span>
        </div>
      )}
    </div>
  )
}
